import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, Marker, Polyline, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { apiPost } from '../../Utils';
import { Spinner } from 'reactstrap';

interface PathSegment {
    type: 'snapped' | 'gap' | 'raw';
    points: { lat: number; lng: number }[];
}

interface Props {
    bookingId: number;
    bookingStatus?: string;
    defaultLat?: number;
    defaultLng?: number;
    mapHeight?: string;
}

const mapContainerStyle = {
    height: '100%',
    width: '100%'
};

const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY || "";

// Free client-side Haversine distance calculator (Zero API calls, Zero cost)
function calculateTotalDistanceKm(points: { lat: number; lng: number }[]): number {
    if (!points || points.length < 2) return 0;
    let total = 0;
    const R = 6371; // Earth radius in km
    for (let i = 0; i < points.length - 1; i++) {
        const dLat = (points[i + 1].lat - points[i].lat) * (Math.PI / 180);
        const dLng = (points[i + 1].lng - points[i].lng) * (Math.PI / 180);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(points[i].lat * (Math.PI / 180)) *
            Math.cos(points[i + 1].lat * (Math.PI / 180)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        total += R * c;
    }
    return total;
}

export default function LocationHistoryMap({ bookingId, bookingStatus, defaultLat, defaultLng, mapHeight = '460px' }: Props) {
    const { isLoaded } = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: apiKey,
    });

    const [loading, setLoading] = useState(true);
    const isInitialCompleted = bookingStatus?.toLowerCase() === 'completed';
    const [pathMode, setPathMode] = useState<'osrm' | 'raw'>(isInitialCompleted ? 'osrm' : 'raw');
    const [isCompleted, setIsCompleted] = useState<boolean>(isInitialCompleted);

    useEffect(() => {
        const isStatusCompleted = bookingStatus?.toLowerCase() === 'completed';
        setPathMode(isStatusCompleted ? 'osrm' : 'raw');
    }, [bookingStatus, bookingId]);

    const [patientSegments, setPatientSegments] = useState<PathSegment[]>([]);
    const [bystanderSegments, setBystanderSegments] = useState<PathSegment[]>([]);
    const [patientRawPath, setPatientRawPath] = useState<{ lat: number; lng: number }[]>([]);
    const [bystanderRawPath, setBystanderRawPath] = useState<{ lat: number; lng: number }[]>([]);

    const [showPatientPath, setShowPatientPath] = useState(true);
    const [showBystanderPath, setShowBystanderPath] = useState(true);
    const [selectedMarker, setSelectedMarker] = useState<string | null>(null);
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
    const controlsRef = useRef<HTMLDivElement>(null);

    // Track when map enters/exits fullscreen view
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    useEffect(() => {
        if (mapInstance && controlsRef.current && window.google) {
            const position = window.google.maps.ControlPosition.TOP_LEFT;
            if (!mapInstance.controls[position].getArray().includes(controlsRef.current)) {
                mapInstance.controls[position].push(controlsRef.current);
            }
        }
    }, [mapInstance, isLoaded]);

    useEffect(() => {
        const fetchHistory = async () => {
            if (!isLoaded) return;
            try {
                const response: any = await apiPost('booking/location-history', { booking_id: bookingId });
                if (response.status && response.data) {
                    const completed = Boolean(response.data.is_completed) || (bookingStatus?.toLowerCase() === 'completed');
                    setIsCompleted(completed);
                    setPathMode(completed ? 'osrm' : 'raw');

                    // Raw GPS points
                    setPatientRawPath(response.data.patient_raw_path || []);
                    setBystanderRawPath(response.data.bystander_raw_path || []);

                    // Extract segments or fallback to flat points
                    let pSegments: PathSegment[] = [];
                    if (response.data.patient_segments && response.data.patient_segments.length > 0) {
                        pSegments = response.data.patient_segments;
                    } else if (response.data.patient_path && response.data.patient_path.length > 0) {
                        pSegments = [{ type: 'snapped', points: response.data.patient_path }];
                    }

                    let bSegments: PathSegment[] = [];
                    if (response.data.bystander_segments && response.data.bystander_segments.length > 0) {
                        bSegments = response.data.bystander_segments;
                    } else if (response.data.bystander_path && response.data.bystander_path.length > 0) {
                        bSegments = [{ type: 'snapped', points: response.data.bystander_path }];
                    }

                    setPatientSegments(pSegments);
                    setBystanderSegments(bSegments);
                }
            } catch (error) {
                console.error("Error fetching location history:", error);
            } finally {
                setLoading(false);
            }
        };

        if (bookingId && isLoaded) {
            fetchHistory();
        } else if (!bookingId) {
            setLoading(false);
        }
    }, [bookingId, isLoaded]);

    const bystanderDistance = useMemo(() => {
        const pts = pathMode === 'raw' ? bystanderRawPath : bystanderSegments.flatMap(s => s.points);
        return calculateTotalDistanceKm(pts);
    }, [pathMode, bystanderRawPath, bystanderSegments]);

    const patientDistance = useMemo(() => {
        const pts = pathMode === 'raw' ? patientRawPath : patientSegments.flatMap(s => s.points);
        return calculateTotalDistanceKm(pts);
    }, [pathMode, patientRawPath, patientSegments]);

    // Fit map bounds helper
    const handleRecenter = useCallback(() => {
        if (!mapInstance) return;
        const allPoints: { lat: number; lng: number }[] = [];

        if (pathMode === 'raw') {
            if (showPatientPath) allPoints.push(...patientRawPath);
            if (showBystanderPath) allPoints.push(...bystanderRawPath);
        } else {
            if (showPatientPath) patientSegments.forEach(seg => allPoints.push(...seg.points));
            if (showBystanderPath) bystanderSegments.forEach(seg => allPoints.push(...seg.points));
        }

        if (allPoints.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            allPoints.forEach(point => bounds.extend(point));

            if (defaultLat && defaultLng) {
                bounds.extend({ lat: defaultLat, lng: defaultLng });
            }

            mapInstance.fitBounds(bounds);
        } else if (defaultLat && defaultLng) {
            mapInstance.panTo({ lat: defaultLat, lng: defaultLng });
            mapInstance.setZoom(15);
        }
    }, [mapInstance, pathMode, showPatientPath, showBystanderPath, patientRawPath, bystanderRawPath, patientSegments, bystanderSegments, defaultLat, defaultLng]);

    // Fit map bounds whenever paths, mode, or map instance change
    useEffect(() => {
        if (mapInstance && isLoaded) {
            handleRecenter();
        }
    }, [handleRecenter, mapInstance, isLoaded]);

    const onLoad = useCallback(function callback(map: google.maps.Map) {
        setMapInstance(map);
    }, []);

    const onUnmount = useCallback(function callback(map: google.maps.Map) {
        setMapInstance(null);
    }, []);

    if (loading || !isLoaded) {
        return <div className="d-flex justify-content-center align-items-center" style={{ height: mapHeight }}><Spinner color="primary" /></div>;
    }

    const center = defaultLat && defaultLng ? { lat: defaultLat, lng: defaultLng } : { lat: 20.5937, lng: 78.9629 };
    const arrowSymbol = window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 2;

    // Get start & end points for patient & bystander
    let firstPatientPt: { lat: number; lng: number } | null = null;
    let lastPatientPt: { lat: number; lng: number } | null = null;
    let firstBystanderPt: { lat: number; lng: number } | null = null;
    let lastBystanderPt: { lat: number; lng: number } | null = null;

    if (pathMode === 'raw') {
        if (patientRawPath.length > 0) {
            firstPatientPt = patientRawPath[0];
            lastPatientPt = patientRawPath[patientRawPath.length - 1];
        }
        if (bystanderRawPath.length > 0) {
            firstBystanderPt = bystanderRawPath[0];
            lastBystanderPt = bystanderRawPath[bystanderRawPath.length - 1];
        }
    } else {
        const flatPatient = patientSegments.flatMap(s => s.points);
        if (flatPatient.length > 0) {
            firstPatientPt = flatPatient[0];
            lastPatientPt = flatPatient[flatPatient.length - 1];
        }
        const flatBystander = bystanderSegments.flatMap(s => s.points);
        if (flatBystander.length > 0) {
            firstBystanderPt = flatBystander[0];
            lastBystanderPt = flatBystander[flatBystander.length - 1];
        }
    }

    const hasMultipleBystanderPts = bystanderRawPath.length > 1 || bystanderSegments.flatMap(s => s.points).length > 1;
    const hasMultiplePatientPts = patientRawPath.length > 1 || patientSegments.flatMap(s => s.points).length > 1;

    return (
        <div style={{ height: mapHeight, width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', position: 'relative' }}>
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={13}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                    streetViewControl: false,  // Street View disabled
                    scaleControl: false,       // Hide distance scale bar
                    keyboardShortcuts: false,  // Hide Keyboard Shortcuts link
                    fullscreenControl: true,   // Fullscreen expand button
                    mapTypeControl: true,      // Satellite / Map toggle
                    mapTypeControlOptions: {
                        position: window.google?.maps?.ControlPosition?.TOP_RIGHT || 3,
                        style: window.google?.maps?.MapTypeControlStyle?.HORIZONTAL_BAR || 1,
                    },
                }}
            >
                {/* OSRM Route Mode */}
                {pathMode === 'osrm' && (
                    <>
                        {/* Render Patient Path Segments */}
                        {showPatientPath && patientSegments.map((segment, idx) => {
                            const isGap = segment.type === 'gap';
                            return (
                                <Polyline
                                    key={`patient-seg-${idx}`}
                                    path={segment.points}
                                    options={{
                                        strokeColor: "#ff0202ff",
                                        strokeWeight: isGap ? 3 : 7,
                                        strokeOpacity: isGap ? 0 : 0.6,
                                        zIndex: isGap ? 1 : 2,
                                        icons: isGap ? [{
                                            icon: {
                                                path: 'M 0,-1 0,1',
                                                strokeOpacity: 0.8,
                                                strokeColor: '#ff0000',
                                                scale: 3
                                            },
                                            offset: '0',
                                            repeat: '15px'
                                        }] : [{
                                            icon: {
                                                path: arrowSymbol,
                                                scale: 3.5,
                                                fillColor: '#ffffff',
                                                fillOpacity: 1,
                                                strokeColor: '#ff0000',
                                                strokeWeight: 2
                                            },
                                            offset: '50px',
                                            repeat: '100px'
                                        }]
                                    }}
                                />
                            );
                        })}

                        {/* Render Bystander Path Segments */}
                        {showBystanderPath && bystanderSegments.map((segment, idx) => {
                            const isGap = segment.type === 'gap';
                            return (
                                <Polyline
                                    key={`bystander-seg-${idx}`}
                                    path={segment.points}
                                    options={{
                                        strokeColor: "#3b82f6",
                                        strokeWeight: isGap ? 3 : 4,
                                        strokeOpacity: isGap ? 0 : 0.9,
                                        zIndex: isGap ? 2 : 3,
                                        icons: isGap ? [{
                                            icon: {
                                                path: 'M 0,-1 0,1',
                                                strokeOpacity: 0.8,
                                                strokeColor: '#1d4ed8',
                                                scale: 3
                                            },
                                            offset: '0',
                                            repeat: '15px'
                                        }] : [{
                                            icon: {
                                                path: arrowSymbol,
                                                scale: 2,
                                                fillColor: '#ffffff',
                                                fillOpacity: 1,
                                                strokeColor: '#1d4ed8',
                                                strokeWeight: 1.5
                                            },
                                            offset: '100%',
                                            repeat: '100px'
                                        }]
                                    }}
                                />
                            );
                        })}
                    </>
                )}

                {/* Raw GPS Mode */}
                {pathMode === 'raw' && (
                    <>
                        {/* Raw Patient Polyline (Wide underneath track like in OSRM) */}
                        {showPatientPath && patientRawPath.length > 0 && (
                            <Polyline
                                path={patientRawPath}
                                options={{
                                    strokeColor: "#ff0202ff",
                                    strokeWeight: 7,
                                    strokeOpacity: 0.6,
                                    zIndex: 2,
                                    icons: [{
                                        icon: {
                                            path: arrowSymbol,
                                            scale: 3.5,
                                            fillColor: '#ffffff',
                                            fillOpacity: 1,
                                            strokeColor: '#ff0000',
                                            strokeWeight: 2
                                        },
                                        offset: '50px',
                                        repeat: '100px'
                                    }]
                                }}
                            />
                        )}

                        {/* Raw Bystander Polyline (Centered on top) */}
                        {showBystanderPath && bystanderRawPath.length > 0 && (
                            <Polyline
                                path={bystanderRawPath}
                                options={{
                                    strokeColor: "#2563eb",
                                    strokeWeight: 4,
                                    strokeOpacity: 0.9,
                                    zIndex: 3,
                                    icons: [{
                                        icon: {
                                            path: arrowSymbol,
                                            scale: 2,
                                            fillColor: '#ffffff',
                                            fillOpacity: 1,
                                            strokeColor: '#1d4ed8',
                                            strokeWeight: 1.5
                                        },
                                        offset: '100%',
                                        repeat: '100px'
                                    }]
                                }}
                            />
                        )}
                    </>
                )}

                {/* Patient Start Marker */}
                {showPatientPath && firstPatientPt && hasMultiplePatientPts && (
                    <Marker
                        position={firstPatientPt}
                        title="Patient Start Point (Click for info)"
                        icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                            scale: 6,
                            fillColor: "#ef4444",
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeWeight: 2,
                        }}
                        onClick={() => setSelectedMarker(selectedMarker === 'patient-start' ? null : 'patient-start')}
                    />
                )}

                {/* Patient End / Current Marker */}
                {showPatientPath && lastPatientPt && (
                    <Marker
                        position={lastPatientPt}
                        title="Patient End / Current Location"
                        icon="https://maps.google.com/mapfiles/ms/icons/red-dot.png"
                        onClick={() => setSelectedMarker(selectedMarker === 'patient' ? null : 'patient')}
                    />
                )}

                {/* Bystander Start Marker */}
                {showBystanderPath && firstBystanderPt && hasMultipleBystanderPts && (
                    <Marker
                        position={firstBystanderPt}
                        title="Bystander Start Point (Click for info)"
                        icon={{
                            path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                            scale: 6,
                            fillColor: "#2563eb",
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeWeight: 2,
                        }}
                        onClick={() => setSelectedMarker(selectedMarker === 'bystander-start' ? null : 'bystander-start')}
                    />
                )}

                {/* Bystander End / Current Marker */}
                {showBystanderPath && lastBystanderPt && (
                    <Marker
                        position={lastBystanderPt}
                        title="Bystander End / Current Location"
                        icon="https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                        onClick={() => setSelectedMarker(selectedMarker === 'bystander' ? null : 'bystander')}
                    />
                )}

                {/* Booked Service Location Marker */}
                {defaultLat && defaultLng && (
                    <Marker
                        position={{ lat: defaultLat, lng: defaultLng }}
                        title="Booked Location (Click for info)"
                        onClick={() => setSelectedMarker(selectedMarker === 'booked' ? null : 'booked')}
                        icon={{
                            path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
                            fillColor: "#8b5cf6",
                            fillOpacity: 1,
                            strokeColor: "#ffffff",
                            strokeWeight: 1.5,
                            scale: 1.5,
                            anchor: new window.google.maps.Point(12, 22),
                            labelOrigin: new window.google.maps.Point(12, -10),
                        }}
                        label={{
                            text: "Booked Location",
                            color: "#5b21b6",
                            fontSize: "11px",
                            fontWeight: "bold",
                        }}
                    />
                )}

                {/* Free Interactive InfoWindows */}
                {selectedMarker === 'patient-start' && firstPatientPt && (
                    <InfoWindow
                        position={firstPatientPt}
                        onCloseClick={() => setSelectedMarker(null)}
                    >
                        <div style={{ padding: '2px 4px', fontSize: '12px', color: '#1f2937' }}>
                            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '2px' }}>🟢 Patient Trip Start</div>
                            <div>Lat: <strong>{firstPatientPt.lat.toFixed(6)}</strong></div>
                            <div>Lng: <strong>{firstPatientPt.lng.toFixed(6)}</strong></div>
                        </div>
                    </InfoWindow>
                )}

                {selectedMarker === 'patient' && lastPatientPt && (
                    <InfoWindow
                        position={lastPatientPt}
                        onCloseClick={() => setSelectedMarker(null)}
                    >
                        <div style={{ padding: '2px 4px', fontSize: '12px', color: '#1f2937' }}>
                            <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '2px' }}>🏁 Patient Final / Current Position</div>
                            <div>Lat: <strong>{lastPatientPt.lat.toFixed(6)}</strong></div>
                            <div>Lng: <strong>{lastPatientPt.lng.toFixed(6)}</strong></div>
                            <div style={{ color: '#4b5563', fontSize: '11px', marginTop: '2px' }}>
                                Total Distance: <strong>{patientDistance.toFixed(2)} km</strong> ({patientRawPath.length} GPS pts)
                            </div>
                        </div>
                    </InfoWindow>
                )}

                {selectedMarker === 'bystander-start' && firstBystanderPt && (
                    <InfoWindow
                        position={firstBystanderPt}
                        onCloseClick={() => setSelectedMarker(null)}
                    >
                        <div style={{ padding: '2px 4px', fontSize: '12px', color: '#1f2937' }}>
                            <div style={{ fontWeight: 600, color: '#2563eb', marginBottom: '2px' }}>🟢 Bystander Trip Start</div>
                            <div>Lat: <strong>{firstBystanderPt.lat.toFixed(6)}</strong></div>
                            <div>Lng: <strong>{firstBystanderPt.lng.toFixed(6)}</strong></div>
                        </div>
                    </InfoWindow>
                )}

                {selectedMarker === 'bystander' && lastBystanderPt && (
                    <InfoWindow
                        position={lastBystanderPt}
                        onCloseClick={() => setSelectedMarker(null)}
                    >
                        <div style={{ padding: '2px 4px', fontSize: '12px', color: '#1f2937' }}>
                            <div style={{ fontWeight: 600, color: '#2563eb', marginBottom: '2px' }}>🏁 Bystander Final / Current Position</div>
                            <div>Lat: <strong>{lastBystanderPt.lat.toFixed(6)}</strong></div>
                            <div>Lng: <strong>{lastBystanderPt.lng.toFixed(6)}</strong></div>
                            <div style={{ color: '#4b5563', fontSize: '11px', marginTop: '2px' }}>
                                Total Distance: <strong>{bystanderDistance.toFixed(2)} km</strong> ({bystanderRawPath.length} GPS pts)
                            </div>
                        </div>
                    </InfoWindow>
                )}

                {selectedMarker === 'booked' && defaultLat && defaultLng && (
                    <InfoWindow
                        position={{ lat: defaultLat, lng: defaultLng }}
                        onCloseClick={() => setSelectedMarker(null)}
                    >
                        <div style={{ padding: '2px 4px', fontSize: '12px', color: '#1f2937' }}>
                            <div style={{ fontWeight: 600, color: '#7c3aed', marginBottom: '2px' }}>🏥 Booked Service Location</div>
                            <div>Lat: <strong>{defaultLat.toFixed(6)}</strong></div>
                            <div>Lng: <strong>{defaultLng.toFixed(6)}</strong></div>
                        </div>
                    </InfoWindow>
                )}

            </GoogleMap>

            {/* Floating Controls Overlay */}
            <div ref={controlsRef} style={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(8px)',
                padding: '6px 12px',
                margin: '10px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '12px',
                border: '1px solid rgba(0,0,0,0.06)',
                zIndex: 10
            }}>
                {/* Switch between OSRM & Raw GPS (Completed Bookings or when raw GPS exists) */}
                {((bookingStatus?.toLowerCase() === 'completed') || isCompleted || patientRawPath.length > 0 || bystanderRawPath.length > 0) && (
                    <>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#f3f4f6',
                            padding: '2px',
                            borderRadius: '6px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <button
                                type="button"
                                onClick={() => setPathMode('osrm')}
                                style={{
                                    padding: '3px 10px',
                                    fontSize: '11px',
                                    fontWeight: pathMode === 'osrm' ? 600 : 500,
                                    backgroundColor: pathMode === 'osrm' ? '#2563eb' : 'transparent',
                                    color: pathMode === 'osrm' ? '#ffffff' : '#4b5563',
                                    borderRadius: '4px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    boxShadow: pathMode === 'osrm' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                    outline: 'none'
                                }}
                            >
                                OSRM Snapped
                            </button>
                            <button
                                type="button"
                                onClick={() => setPathMode('raw')}
                                style={{
                                    padding: '3px 10px',
                                    fontSize: '11px',
                                    fontWeight: pathMode === 'raw' ? 600 : 500,
                                    backgroundColor: pathMode === 'raw' ? '#2563eb' : 'transparent',
                                    color: pathMode === 'raw' ? '#ffffff' : '#4b5563',
                                    borderRadius: '4px',
                                    border: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                    boxShadow: pathMode === 'raw' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                    outline: 'none'
                                }}
                            >
                                Raw GPS
                            </button>
                        </div>
                        <div style={{ width: '1px', height: '14px', backgroundColor: '#e5e7eb' }}></div>
                    </>
                )}

                {/* Patient Path Toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, cursor: 'pointer', userSelect: 'none' }}>
                    <input
                        type="checkbox"
                        checked={showPatientPath}
                        onChange={(e) => setShowPatientPath(e.target.checked)}
                        style={{ accentColor: '#ef4444', width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    <div style={{ width: '12px', height: '3px', backgroundColor: '#ef4444', opacity: 0.8, borderRadius: '1.5px' }}></div>
                    <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                        Patient {isFullscreen && patientDistance > 0 && `(${patientDistance.toFixed(1)} km)`}
                    </span>
                </label>

                <div style={{ width: '1px', height: '14px', backgroundColor: '#e5e7eb' }}></div>

                {/* Bystander Path Toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, cursor: 'pointer', userSelect: 'none' }}>
                    <input
                        type="checkbox"
                        checked={showBystanderPath}
                        onChange={(e) => setShowBystanderPath(e.target.checked)}
                        style={{ accentColor: '#3b82f6', width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    <div style={{ width: '12px', height: '3px', backgroundColor: '#3b82f6', opacity: 0.8, borderRadius: '1.5px' }}></div>
                    <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
                        Bystander {isFullscreen && bystanderDistance > 0 && `(${bystanderDistance.toFixed(1)} km)`}
                    </span>
                </label>

                {/* 🎯 Fit Route Button (Displayed ONLY in Fullscreen view) */}
                {isFullscreen && (
                    <>
                        <div style={{ width: '1px', height: '14px', backgroundColor: '#e5e7eb' }}></div>
                        <button
                            type="button"
                            onClick={handleRecenter}
                            title="Fit whole trip route into map view"
                            style={{
                                padding: '3px 8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: '#ffffff',
                                color: '#374151',
                                borderRadius: '4px',
                                border: '1px solid #d1d5db',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                outline: 'none'
                            }}
                        >
                            🎯 Fit Route
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
